import React, { useState, useEffect, useRef } from 'react';
import { BiChevronLeft, BiChevronRight } from 'react-icons/bi';

export default function Carousel({ children, autoPlay = true, interval = 5000, minSlideWidth = 350 }) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isHovering, setIsHovering] = useState(false);
    const [visibleSlides, setVisibleSlides] = useState(1);
    const containerRef = useRef(null);

    const totalSlides = React.Children.count(children);
    const maxIndex = Math.max(0, totalSlides - visibleSlides);

    // Calcular automáticamente los slides visibles según el ancho del contenedor
    useEffect(() => {
        const calculateVisibleSlides = () => {
            if (containerRef.current) {
                const containerWidth = containerRef.current.offsetWidth;
                const gap = 20; // Gap entre slides
                const calculated = Math.max(1, Math.floor((containerWidth + gap) / (minSlideWidth + gap)));
                setVisibleSlides(Math.min(calculated, totalSlides));
            }
        };

        calculateVisibleSlides();
        window.addEventListener('resize', calculateVisibleSlides);
        return () => window.removeEventListener('resize', calculateVisibleSlides);
    }, [minSlideWidth, totalSlides]);

    // Auto play
    React.useEffect(() => {
        if (!autoPlay || isHovering || totalSlides === 0) return;

        const timer = setInterval(() => {
            setCurrentIndex((prev) => {
                const next = prev + 1;
                return next > maxIndex ? 0 : next;
            });
        }, interval);

        return () => clearInterval(timer);
    }, [autoPlay, interval, isHovering, maxIndex, totalSlides]);

    const goToPrevious = () => {
        setCurrentIndex((prev) => (prev === 0 ? maxIndex : prev - 1));
    };

    const goToNext = () => {
        setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
    };

    if (totalSlides === 0) {
        return null;
    }

    return (
        <div
            ref={containerRef}
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
            style={{
                position: 'relative',
                width: '100%',
                overflow: 'hidden',
                padding: '2rem 0'
            }}
        >
            {/* Contenedor de slides */}
            <div
                style={{
                    display: 'flex',
                    transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: `translateX(-${currentIndex * (100 / visibleSlides)}%)`,
                    gap: '20px',
                    padding: '0 10px'
                }}
            >
                {React.Children.map(children, (child, index) => (
                    <div
                        key={index}
                        style={{
                            flex: `0 0 calc(${100 / visibleSlides}% - ${(20 * (visibleSlides - 1)) / visibleSlides}px)`,
                            minWidth: 0
                        }}
                    >
                        {child}
                    </div>
                ))}
            </div>

            {/* Botón anterior */}
            {totalSlides > visibleSlides && (
                <>
                    <button
                        onClick={goToPrevious}
                        disabled={currentIndex === 0}
                        style={{
                            position: 'absolute',
                            left: '-20px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: '56px',
                            height: '56px',
                            borderRadius: '50%',
                            border: 'none',
                            backgroundColor: '#fff',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#028322',
                            transition: 'all 0.2s ease',
                            opacity: currentIndex === 0 ? 0.5 : 1,
                            zIndex: 10
                        }}
                        onMouseEnter={(e) => {
                            if (currentIndex !== 0) {
                                e.currentTarget.style.backgroundColor = '#028322';
                                e.currentTarget.style.color = '#fff';
                                e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#fff';
                            e.currentTarget.style.color = '#028322';
                            e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                        }}
                    >
                        <BiChevronLeft size={32} />
                    </button>

                    {/* Botón siguiente */}
                    <button
                        onClick={goToNext}
                        disabled={currentIndex >= maxIndex}
                        style={{
                            position: 'absolute',
                            right: '-20px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: '56px',
                            height: '56px',
                            borderRadius: '50%',
                            border: 'none',
                            backgroundColor: '#fff',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#028322',
                            transition: 'all 0.2s ease',
                            opacity: currentIndex >= maxIndex ? 0.5 : 1,
                            zIndex: 10
                        }}
                        onMouseEnter={(e) => {
                            if (currentIndex < maxIndex) {
                                e.currentTarget.style.backgroundColor = '#028322';
                                e.currentTarget.style.color = '#fff';
                                e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#fff';
                            e.currentTarget.style.color = '#028322';
                            e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                        }}
                    >
                        <BiChevronRight size={32} />
                    </button>
                </>
            )}

            {/* Indicadores */}
            {totalSlides > visibleSlides && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '8px',
                        marginTop: '1.5rem'
                    }}
                >
                    {Array.from({ length: maxIndex + 1 }).map((_, index) => (
                        <button
                            key={index}
                            onClick={() => setCurrentIndex(index)}
                            style={{
                                width: currentIndex === index ? '32px' : '8px',
                                height: '8px',
                                borderRadius: '4px',
                                border: 'none',
                                backgroundColor: currentIndex === index ? '#028322' : '#e5e7eb',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                padding: 0
                            }}
                            aria-label={`Ir al slide ${index + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
